import { cn } from '../../lib/css/className/className';
import { Color } from '../../internal/types/designSystemTypes';
import './typography.css';
import './typographyWithSkin.css';

const cls = cn('typography');

export type TypographyTextPresetWithSkin =
    | 'current-main'
    | 'current-body'
    | 'current-caption'
    | 'current-main + current-body'
    | 'current-main + current-caption';

export type TypographyRole = 'main' | 'supplementary' | 'placeholder';

export interface TypographyOptions {
    /**
     * Цветовая палитра для текста и описания.
     * Перечисленные цвета определены дизайн-системой
     */
    color?: Color | 'current';

    /**
     * Размер шрифта и высота строки для текста.
     * Значения с парами предназначены для задания стиля основному тексту и описанию.
     * Перечисленные сочетания размеров определены дизайн-системой
     */
    size?: TypographyTextPresetWithSkin;

    /**
     * Переключает декорирование для интеративного текста (при наведении на который всплывают popup). Не для ссылок.
     * Применяется в следующих случаях: https://nda.ya.ru/t/pKMtdVcQ753mav
     */
    interactive?: boolean;

    /**
     * Роль элемента типографики: основной (main) или дополнительный (supplementary) текст.
     * От роли зависят возвращаемые textClasses для одиночного текстового элемента
     * @default 'main'
     */
    role?: TypographyRole;

    /**
     * Жирность текста
     */
    bold?: boolean;
}

/**
 * React Hook для применения стилей типографики
 * Задает свойства:
 * - font-family
 * - font-size
 * - font-weight
 * - line-height
 * - color
 * - text-decoration
 */
export const useTypographyWithSkin = (options: TypographyOptions) => {
    const { size, color, interactive, role = 'main', bold } = options;
    const [mainTextSize, captionSize] = size?.split(' + ') ?? [];

    const textClasses = cls({ size: mainTextSize, role: role, color: color, interactive, bold });

    if (!captionSize) {
        return { textClasses };
    }

    const captionClasses = cls({ size: captionSize, role: 'supplementary', color: color });
    return { textClasses, captionClasses };
};
